import { Navigate, useParams } from "react-router-dom";
import { useT } from "@/i18n";
import { ErrorState, LoadingState } from "@/components/ui/states";
import { ApiClientError } from "@/lib/api";
import { useClient } from "@/features/clients/hooks";
import { useOpportunity } from "@/features/opportunities/hooks";

/**
 * The old UUID detail routes, kept alive as redirects.
 *
 * `/opportunities/<uuid>` and `/clients/<uuid>` are in bookmarks, in shared links and in the
 * end-to-end suite. Retiring them outright would break all three to save two small
 * components — so they fetch the record and replace themselves with its slug URL.
 *
 * `replace`, so the UUID never sits in history: pressing Back from the modal should return
 * to wherever the reader came from, not bounce through the address they arrived by.
 */
export function OpportunityIdRedirect(): React.JSX.Element {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const query = useOpportunity(id);

  if (query.isPending) return <LoadingState rows={4} label={t("opportunity.loading")} />;

  if (query.error || !query.data) {
    return (
      <ErrorState
        message={query.error instanceof ApiClientError ? query.error.message : t("opportunity.loadFailed")}
        requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
        onRetry={() => void query.refetch()}
      />
    );
  }

  return <Navigate to={`/opportunity/${query.data.slug}`} replace />;
}

export function ClientIdRedirect(): React.JSX.Element {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const query = useClient(id);

  if (query.isPending) return <LoadingState rows={4} label={t("client.loading")} />;

  if (query.error || !query.data) {
    return (
      <ErrorState
        message={query.error instanceof ApiClientError ? query.error.message : t("client.loadFailed")}
        requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
        onRetry={() => void query.refetch()}
      />
    );
  }

  return <Navigate to={`/client/${query.data.slug}`} replace />;
}
